/**
 * A renderelési réteg: jelenet, kamera, fények, aktormodellek, utómunka.
 *
 * A játéklogika ezt a modult NEM ismeri — csak a `CameraState`-et írja és az
 * `FxApi`-t hívja. Cserébe itt szabadon lehet a látványon dolgozni anélkül,
 * hogy bármit el lehetne rontani a szimulációban.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type { Actor, CameraState, GameEventMap } from '../core/contracts'
import type { EventBus } from '../core/events'
import type { LevelDef } from '../world/level'
import { buildEnemyModel, buildPickupModel, pickupLightColor, hasEnemyModel, type ActorModel } from '../assets/models'
import { PostFx, DEFAULT_POSTFX, type PostFxSettings } from './postfx'
import { Effects } from './effects'
import { ViewModel } from './viewmodel'
import { Rng } from '../core/rng'
import { clamp, damp, TAU } from '../core/math'

interface ActorView {
  root: THREE.Group
  model: ActorModel | null
  /** A fény színe, ha ez az aktor fényforrás. A fényt a készlet adja. */
  lightColor: number | null
  lightRadius: number
  /** Talajra vetett folt-árnyék — X jogos követelménye volt. */
  shadow: THREE.Mesh | null
  spin: number
  bobPhase: number
  painDecay: number
}

/**
 * Egy pályafény ADATA. Fontos: ez már nem tartalmaz Three.js fényobjektumot.
 *
 * A pályákon 13–19 fényforrás van; a mozgó és villanó fényekkel együtt ez
 * 34 pontfényt jelentett egyszerre. Előrenderelt (forward) megvilágításnál
 * MINDEN képpont fragmens-shadere végigmegy az ÖSSZES fényen — 34 fény
 * 1080p-n nagyságrendekkel drágább, mint 8, és egy gyengébb GPU-n a
 * meghajtó időtúllépését (és ezzel WebGL-kontextusvesztést) okozhatja.
 * Ezért a pályafényekből is fix méretű készletet tartunk, és keretenként
 * a kamerához legközelebbieket kapcsoljuk rá.
 */
interface LevelLight {
  x: number
  y: number
  z: number
  color: number
  distance: number
  base: number
  style: 'steady' | 'flicker' | 'pulse'
  phase: number
}

/** Ennyi pályafény éghet egyszerre. A többi a távolság alapján kimarad. */
const LEVEL_LIGHT_SLOTS = 8

interface LightSlot {
  light: THREE.PointLight
  /** A `levelLights` indexe, vagy -1. */
  owner: number
  /** Az aktuális, simított erősség — a be-/kikapcsolás sosem ugrik. */
  current: number
}

/**
 * Fényerő-átváltó a pályaadat és a Three.js között.
 *
 * A Three r155 óta fizikai fényegységeket használ: a pontfény besugárzása
 * `intenzitás / távolság²`. Egy 10 egységre lévő lámpához így ~100 candela
 * kellene, ami pályaszerkesztés közben értelmezhetetlen szám. Ezért a
 * `LevelDef.lights[].intensity` mezőt „kb. mennyire világítja meg a 3-4 egységre
 * lévő falat" értelemben használjuk, és itt váltjuk át. Egy `intensity: 12`-es
 * lámpa 4 egységről ~1.2 besugárzást ad — vagyis nagyjából teljesen kivilágítja
 * a falat, de nem égeti ki.
 */
const LIGHT_UNIT = 6.2

/**
 * Ennyi mozgó fényforrást tudunk egyszerre megjeleníteni (felvehető tárgyak,
 * lövedékek). Fix szám, mert a Three.js a LÁTHATÓ fények számából képzi a
 * shader-program kulcsát: ha aktoronként hoznánk létre és szüntetnénk meg
 * fényt, minden egyes tárgyfelvétel és lövedék-becsapódás újrafordítaná a
 * jelenet összes shaderét — látható akadással.
 */
const DYNAMIC_LIGHTS = 5

/** Egyszerű, olcsó folt-árnyék textúra — egyszer generáljuk. */
function makeShadowTexture(): THREE.CanvasTexture {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const cx = canvas.getContext('2d')!
  const grad = cx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  grad.addColorStop(0, 'rgba(0,0,0,0.75)')
  grad.addColorStop(0.55, 'rgba(0,0,0,0.35)')
  grad.addColorStop(1, 'rgba(0,0,0,0)')
  cx.fillStyle = grad
  cx.fillRect(0, 0, size, size)
  return new THREE.CanvasTexture(canvas)
}

export class Renderer {
  readonly renderer: THREE.WebGLRenderer
  readonly scene = new THREE.Scene()
  readonly camera: THREE.PerspectiveCamera
  readonly effects: Effects
  readonly viewModel = new ViewModel()
  readonly postfx: PostFx

  /** A kézifegyver külön jelenetben és kamerával: soha nem lóg falba. */
  private vmScene = new THREE.Scene()
  private vmCamera: THREE.PerspectiveCamera
  private vmLight: THREE.PointLight

  private ambient: THREE.AmbientLight
  private hemi: THREE.HemisphereLight
  /**
   * A játékossal együtt mozgó, halvány fény.
   *
   * Nem „zseblámpa": a hatótávja rövid, a célja kizárólag az, hogy a
   * közvetlen közeledben lévő geometria és ellenfél OLVASHATÓ legyen.
   * Enélkül vagy az egész pályát fel kell világítani (és elvész a
   * fény-sötét kontraszt a termek között), vagy az orrod előtt álló
   * szörny fekete folt marad. Ez a kettő közti helyes kompromisszum.
   */
  private playerLight: THREE.PointLight
  /** Fix méretű, mindig a jelenetben lévő fénykészlet a mozgó forrásokhoz. */
  private dynamicLights: THREE.PointLight[] = []
  private lightCandidates: { actor: Actor; view: ActorView; dist: number }[] = []
  /**
   * Melyik aktorhoz tartozik jelenleg az adott fény. A hiszterézishez kell:
   * a már kiosztott forrás előnyt kap, így két közel azonos távolságú tárgy
   * között a fény nem ugrál keretenként. (X `19-X-revizio-2.md` F2.)
   */
  private lightOwner: number[] = []
  private levelLights: LevelLight[] = []
  private levelSlots: LightSlot[] = []
  private levelOrder: number[] = []
  private levelGroup: THREE.Group | null = null
  private views = new Map<number, ActorView>()
  private shadowTex = makeShadowTexture()
  private shadowMat: THREE.MeshBasicMaterial

  /** A világ-menet rajzolási statisztikája (a diagnosztikai kijelzőhöz). */
  readonly sceneStats = { calls: 0, triangles: 0 }

  private shakeAmount = 0
  private shakeSeed = new Rng(0x5eed)
  private time = 0
  private rng: Rng

  constructor(canvas: HTMLCanvasElement, events: EventBus<GameEventMap>, rng: Rng, settings: PostFxSettings = DEFAULT_POSTFX) {
    this.rng = rng
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false, // A retro kép szándékosan éles; az AA elmosná.
      powerPreference: 'high-performance',
      stencil: false,
    })
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setSize(window.innerWidth, window.innerHeight, false)
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    // Filmes tónusleképezés. Enélkül minden 1.0 fölötti besugárzás vakfehérre
    // vágódik — és a pontfény 1/d² jellege miatt egy közel álló ellenfél
    // óhatatlanul túllépi az 1.0-t. Az ACES a csúcsokat lekerekíti, így a
    // közeli lény világos marad, de megmarad a formája és a színe.
    // A világító részek (szem, torkolattűz, nyomjelző) `toneMapped: false`
    // anyagot használnak, ezért továbbra is teljes erővel szólnak.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0
    this.renderer.autoClear = false

    this.camera = new THREE.PerspectiveCamera(78, window.innerWidth / window.innerHeight, 0.05, 260)
    this.vmCamera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 0.01, 6)

    this.ambient = new THREE.AmbientLight(0xffffff, 0.75)
    this.hemi = new THREE.HemisphereLight(0x7d8a99, 0x2e2018, 0.58)
    this.scene.add(this.ambient, this.hemi)

    this.playerLight = new THREE.PointLight(0xffe2bc, 42, 18, 2)
    this.scene.add(this.playerLight)

    for (let i = 0; i < DYNAMIC_LIGHTS; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 6, 2)
      this.scene.add(l)
      this.dynamicLights.push(l)
      this.lightOwner.push(-1)
    }

    for (let i = 0; i < LEVEL_LIGHT_SLOTS; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 10, 2)
      this.scene.add(l)
      this.levelSlots.push({ light: l, owner: -1, current: 0 })
    }

    this.effects = new Effects(rng)
    this.scene.add(this.effects.group)

    // A kézifegyver jelenetének saját, stabil megvilágítása — így a fegyver
    // sosem tűnik el egy sötét szobában, de követi a torkolattüzet.
    //
    // FONTOS: a fegyver és a fényei a KAMERÁHOZ vannak csatolva, nem a
    // jelenethez. Ha fix jelenetpozícióban lennének, a kamera elfordulásakor
    // a fegyver a kamera mögé kerülne (és a megvilágítása is elfordulna
    // hozzá képest).
    this.vmScene.add(new THREE.AmbientLight(0xffffff, 0.85))
    const key = new THREE.DirectionalLight(0xfff0dd, 1.9)
    key.position.set(-0.6, 1, 0.7)
    this.vmCamera.add(key)
    const rim = new THREE.DirectionalLight(0x6699cc, 0.9)
    rim.position.set(0.9, -0.2, -0.8)
    this.vmCamera.add(rim)
    this.vmLight = new THREE.PointLight(0xffaa44, 0, 2.6, 2)
    this.vmLight.position.set(0.2, -0.1, -0.7)
    this.vmCamera.add(this.vmLight)
    this.vmCamera.add(this.viewModel.group)
    this.vmScene.add(this.vmCamera)

    this.shadowMat = new THREE.MeshBasicMaterial({
      map: this.shadowTex,
      transparent: true,
      depthWrite: false,
      opacity: 0.85,
    })

    this.postfx = new PostFx(this.renderer, settings)

    // Diagnosztikai kapaszkodó: a füstteszt innen olvassa a rajzolási
    // statisztikát és a lefordított shader-programok számát.
    ;(window as unknown as { __renderer?: THREE.WebGLRenderer }).__renderer = this.renderer

    events.on('spawn', ({ actor }) => this.onSpawn(actor))
    events.on('damage', ({ target }) => {
      const v = this.views.get(target.id)
      if (v) v.painDecay = 1
    })

    window.addEventListener('resize', () => this.resize())
  }

  resize(): void {
    const w = window.innerWidth
    const h = window.innerHeight
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.vmCamera.aspect = w / h
    this.vmCamera.updateProjectionMatrix()
    const size = new THREE.Vector2()
    this.renderer.getDrawingBufferSize(size)
    this.postfx.setSize(size.x, size.y)
  }

  // -------------------------------------------------------------------------
  // Pálya
  // -------------------------------------------------------------------------

  loadLevel(def: LevelDef, group: THREE.Group): void {
    this.unloadLevel()
    this.levelGroup = group
    this.scene.add(group)

    this.scene.background = new THREE.Color(def.skyColor)
    this.scene.fog = def.fogDensity > 0 ? new THREE.FogExp2(def.fogColor, def.fogDensity) : null
    // A pályaadat `ambient` mezője 0..1 „hangulati" érték; a Three-nek ennél
    // magasabb tényleges intenzitás kell, különben a sötét paletta feketébe fullad.
    // Az ambiens szándékosan alacsony: a tér formáját a PONTFÉNYEK adják.
    // Ha az ambiens dominál, minden szoba egyformán szürke lesz, és elvész
    // a Doom-ra jellemző fény-sötét kontraszt a helyiségek között.
    this.ambient.intensity = 0.72 + def.ambient * 0.96
    // A féggömbfény a szoba-fényektől függetlenül tartja olvashatóan a
    // padló/mennyezet irányt — enélkül a fényforrásoktól távol minden egybefolyik.
    this.hemi.intensity = 0.48 + def.ambient * 0.62

    for (const l of def.lights) {
      this.levelLights.push({
        x: l.pos.x,
        y: l.y,
        z: l.pos.z,
        color: l.color,
        distance: l.distance,
        base: l.intensity * LIGHT_UNIT,
        style: l.style ?? 'steady',
        phase: this.rng.next() * TAU,
      })
    }
    this.levelOrder = this.levelLights.map((_, i) => i)
    for (const s of this.levelSlots) {
      s.owner = -1
      s.current = 0
      s.light.intensity = 0
    }

  }

  unloadLevel(): void {
    if (this.levelGroup) {
      this.scene.remove(this.levelGroup)
      this.levelGroup = null
    }
    this.levelLights.length = 0
    this.levelOrder.length = 0
    for (const s of this.levelSlots) {
      s.owner = -1
      s.current = 0
      s.light.intensity = 0
    }
    for (const [id] of this.views) this.removeView(id)
    this.effects.clear()
    this.viewModel.reset()
  }

  /**
   * Shader-előmelegítés. A pályabetöltés UTOLSÓ lépéseként hívandó —
   * akkor, amikor már minden aktor létrejött és a kézifegyver is fel van
   * szerelve.
   *
   * Miért nem elég a `renderer.compile()` önmagában: az `traverseVisible`-lel
   * jár, ezért a `visible = false` állapotú pool-elemeket (nyomjelző,
   * becsapódás-nyom) kihagyja, és azok csak az első lövéskor fordulnának le.
   * Ezért itt mindent ideiglenesen láthatóvá teszünk, lefordítunk, RAJZOLUNK
   * is egy képkockát (mert egyes meghajtók a linkelést a első rajzolásig
   * halasztják), majd visszaállítunk.
   *
   * Ez a javítás X `19-X-revizio-2.md` F1 találatára válasz.
   */
  prewarm(): void {
    const hidden: THREE.Object3D[] = []
    const collect = (root: THREE.Object3D): void => {
      root.traverse((o) => {
        if (!o.visible) {
          hidden.push(o)
          o.visible = true
        }
      })
    }
    this.effects.prewarmVisible()
    collect(this.scene)
    collect(this.vmScene)

    this.renderer.compile(this.scene, this.camera)
    this.renderer.compile(this.vmScene, this.vmCamera)

    this.postfx.renderTo((r) => {
      r.render(this.scene, this.camera)
      r.clearDepth()
      r.render(this.vmScene, this.vmCamera)
    })

    for (const o of hidden) o.visible = false
    this.effects.clear()
  }

  // -------------------------------------------------------------------------
  // Aktornézetek
  // -------------------------------------------------------------------------

  private onSpawn(actor: Actor): void {
    if (actor.kind === 'player') return

    const root = new THREE.Group()
    root.name = `actor:${actor.id}:${actor.type}`
    let model: ActorModel | null = null
    let lightColor: number | null = null
    let lightRadius = 4
    let shadow: THREE.Mesh | null = null

    if (actor.kind === 'enemy' && hasEnemyModel(actor.type)) {
      model = buildEnemyModel(actor.type)
      root.add(model.root)
      // Folt-árnyék: enélkül a figura „lebeg" a padlón.
      shadow = new THREE.Mesh(new THREE.PlaneGeometry(actor.radius * 3.4, actor.radius * 3.4), this.shadowMat)
      shadow.rotation.x = -Math.PI / 2
      shadow.position.y = 0.02
      root.add(shadow)
    } else if (actor.kind === 'pickup') {
      root.add(buildPickupModel(actor.type))
      lightColor = pickupLightColor(actor.type)
      lightRadius = 3.6
    } else if (actor.kind === 'projectile') {
      const color = projectileColor(actor.type)
      const core = new THREE.Mesh(
        new THREE.IcosahedronGeometry(actor.radius * 1.5, 0),
        new THREE.MeshBasicMaterial({ color, toneMapped: false }),
      )
      root.add(core)
      lightColor = color
      lightRadius = 7
    } else if (actor.kind === 'decor') {
      root.add(buildPickupModel(actor.type))
    } else {
      return
    }

    this.scene.add(root)
    const view: ActorView = {
      root,
      model,
      lightColor,
      lightRadius,
      shadow,
      spin: this.rng.next() * TAU,
      bobPhase: this.rng.next() * TAU,
      painDecay: 0,
    }
    this.views.set(actor.id, view)
    actor.view = view
  }

  private removeView(id: number): void {
    const v = this.views.get(id)
    if (!v) return
    this.scene.remove(v.root)
    v.model?.dispose()
    v.root.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material !== this.shadowMat) o.geometry.dispose()
    })
    this.views.delete(id)
  }

  /** Az eltűnt aktorok nézeteinek takarítása. */
  syncActors(
    actors: readonly Actor[],
    alpha: number,
    dtReal: number,
    floorAt: (x: number, z: number, hint?: number) => number,
    hazardAt: (x: number, z: number, hint?: number) => number,
  ): void {
    const alive = new Set<number>()

    for (const a of actors) {
      alive.add(a.id)
      const v = this.views.get(a.id)
      if (!v) continue

      // Interpoláció az előző és aktuális szimulációs állapot között —
      // ettől lesz sima a kép 144 Hz-en is, 60 Hz-es szimuláció mellett.
      const x = a.prevPos.x + (a.pos.x - a.prevPos.x) * alpha
      let y = a.prevPos.y + (a.pos.y - a.prevPos.y) * alpha
      const z = a.prevPos.z + (a.pos.z - a.prevPos.z) * alpha

      // Savas/mérgező térben a test SÜLLYED egy kicsit: a padló ott a
      // folyadék felszíne, ezért enélkül úgy néz ki, mintha a lény a
      // folyadék TETEJÉN sétálna. Ennyi elég ahhoz, hogy gázolásnak
      // olvasódjon; többtől már beleveszne a sziluett.
      const wading = a.onGround && hazardAt(a.pos.x, a.pos.z, a.roomId) > 0
      if (wading) y -= a.height > 1.6 ? 0.34 : 0.22
      v.root.position.set(x, y, z)

      if (a.kind === 'pickup' || a.kind === 'decor') {
        // A tárgyak lassan pörögnek és lebegnek: a szem megtalálja őket.
        v.spin += dtReal * 1.5
        v.bobPhase += dtReal * 2.2
        v.root.rotation.y = v.spin
        v.root.position.y = y + 0.45 + Math.sin(v.bobPhase) * 0.09
      } else if (a.kind === 'projectile') {
        v.root.rotation.y += dtReal * 9
        v.root.rotation.x += dtReal * 7
        this.effects.projectileTrail(v.root.position, projectileColor(a.type), 0.11)
      } else {
        let yaw = a.prevYaw + shortestAngle(a.prevYaw, a.yaw) * alpha
        v.root.rotation.y = yaw

        if (v.painDecay > 0) v.painDecay = Math.max(0, v.painDecay - dtReal * 5)

        if (v.model) {
          const speed = Math.hypot(a.vel.x, a.vel.z)
          v.model.update({
            speed,
            telegraph: clamp(a.anim.telegraph, 0, 1),
            pain: a.alive ? Math.max(v.painDecay, clamp(a.anim.pain, 0, 1)) : 0,
            death: a.alive ? 0 : clamp(a.deadFor / 42, 0, 1),
            time: this.time,
          })
        }

        // Folyadékban nincs vetett árnyék — a felszín szórja a fényt.
        if (v.shadow) v.shadow.visible = !wading
        if (v.shadow && !wading) {
          // Az árnyék a padlón marad, akkor is, ha a lény ugrik vagy esik.
          const fy = floorAt(a.pos.x, a.pos.z, a.roomId)
          v.shadow.position.y = fy - y + 0.02
          const h = Math.max(0, y - fy)
          const k = clamp(1 - h / 3, 0.25, 1)
          v.shadow.scale.setScalar(k)
          ;(v.shadow.material as THREE.MeshBasicMaterial).opacity = 0.85 * k
        }
      }
    }

    for (const [id] of this.views) {
      if (!alive.has(id)) this.removeView(id)
    }

    this.assignDynamicLights(actors)
  }

  /**
   * A fénykészlet kiosztása a kamerához legközelebbi fényforrásoknak.
   *
   * A készlet mérete állandó, a fények soha nem kerülnek ki a jelenetből —
   * csak a pozíciójuk, színük és erősségük változik. Így a Three.js
   * fényszáma sosem változik, és nincs shader-újrafordítás.
   */
  private assignDynamicLights(actors: readonly Actor[]): void {
    const cam = this.camera.position
    this.lightCandidates.length = 0

    for (const a of actors) {
      if (!a.alive) continue
      const v = this.views.get(a.id)
      if (!v || v.lightColor === null) continue
      const d = v.root.position.distanceToSquared(cam)
      // A látótávon kívüli forrás úgysem látszana.
      if (d > 60 * 60) continue
      this.lightCandidates.push({ actor: a, view: v, dist: d })
    }

    // A lövedék mindig fontosabb, mint egy álló tárgy: az mozog és a
    // játékosra tart, tehát a fénye információt hordoz.
    // A már fényt kapott forrás 25%-nyi „kedvezményt" kap a távolságában.
    // Enélkül két hasonló távolságú tárgy között a fény keretenként
    // átugrálna, ami villódzásnak látszik.
    const held = new Set(this.lightOwner)
    this.lightCandidates.sort((p, q) => {
      const pp = p.actor.kind === 'projectile' ? 0 : 1
      const qq = q.actor.kind === 'projectile' ? 0 : 1
      if (pp !== qq) return pp - qq
      const pd = held.has(p.actor.id) ? p.dist * 0.75 : p.dist
      const qd = held.has(q.actor.id) ? q.dist * 0.75 : q.dist
      return pd - qd
    })

    for (let i = 0; i < this.dynamicLights.length; i++) {
      const l = this.dynamicLights[i]
      const c = this.lightCandidates[i]
      if (!c) {
        l.intensity = 0
        this.lightOwner[i] = -1
        continue
      }
      this.lightOwner[i] = c.actor.id
      l.color.setHex(c.view.lightColor!)
      l.distance = c.view.lightRadius
      l.position.copy(c.view.root.position)
      if (c.actor.kind === 'pickup') {
        l.position.y += 0.3
        l.intensity = 2.4 + Math.sin(c.view.bobPhase * 1.7) * 0.7
      } else {
        l.intensity = 7
      }
    }
  }

  // -------------------------------------------------------------------------
  // Kamera és rajzolás
  // -------------------------------------------------------------------------

  shake(amount: number): void {
    this.shakeAmount = Math.min(1.4, this.shakeAmount + amount)
  }

  muzzleLight(intensity: number): void {
    this.vmLight.intensity = intensity
  }

  private applyCamera(cam: CameraState, dtReal: number): void {
    this.camera.position.copy(cam.pos)
    this.camera.rotation.order = 'YXZ'
    // A szerződés szerint `yaw = 0` a +Z felé néz (ez az irány egyezik az
    // ellenségmodellek és a lövés-sugarak konvenciójával). A Three kamerája
    // viszont alapállásban a saját -Z tengelye felé néz, ezért kell a π.
    // Enélkül a W hátrafelé visz, mert a mozgásvektor és a nézet szembemegy.
    this.camera.rotation.set(cam.pitch, cam.yaw + Math.PI, cam.roll)

    if (Math.abs(this.camera.fov - cam.fov) > 0.01) {
      this.camera.fov = cam.fov
      this.camera.updateProjectionMatrix()
    }

    if (this.shakeAmount > 0.0005) {
      // Nagyfrekvenciás, gyorsan lecsengő rázás. Az elmozdulás és az
      // elfordulás együtt kell: csak elmozdulás „ugrálásnak" érződik.
      const s = this.shakeAmount
      const r = this.shakeSeed
      this.camera.position.x += r.spread(0.06) * s
      this.camera.position.y += r.spread(0.06) * s
      this.camera.position.z += r.spread(0.06) * s
      this.camera.rotation.z += r.spread(0.02) * s
      this.camera.rotation.x += r.spread(0.012) * s
      this.shakeAmount = damp(this.shakeAmount, 0, 0.00008, dtReal)
    } else {
      this.shakeAmount = 0
    }

    // A játékos fénye kicsit előbbre és lejjebb, mint a szem: így a padló
    // és a szemközti fal is kap belőle, de nem világítja túl a fegyvert.
    // A fényforrás a fej MÖGÉ kerül: a 1/d² miatt így az orrunk előtt álló
    // ellenfél sem esik a fény tövébe, viszont az előttünk lévő padló
    // és fal ugyanúgy megvilágítottá válik.
    this.playerLight.position.set(
      cam.pos.x - Math.sin(cam.yaw) * 1.3,
      cam.pos.y + 0.25,
      cam.pos.z - Math.cos(cam.yaw) * 1.3,
    )

    this.vmCamera.rotation.order = 'YXZ'
    this.vmCamera.rotation.copy(this.camera.rotation)
    this.vmCamera.updateMatrixWorld(true)
    // A fegyver-kamera origóban marad, de a torkolat világpozíciójához
    // tudni kell, hol áll a valódi kamera.
    this.viewModel.worldOffset.copy(this.camera.position)
  }

  /**
   * A pályafény-készlet kiosztása és animálása.
   *
   * A kamerához legközelebbi `LEVEL_LIGHT_SLOTS` fény ég. Ha egy fény
   * kikerül a készletből, NEM azonnal alszik el: az erőssége lecseng, és a
   * foglalat csak nulla közelében vált gazdát. Enélkül a fények
   * be-/kivillannának mozgás közben, ami sokkal feltűnőbb lenne, mint az,
   * hogy egy távoli lámpa nem világít.
   */
  private updateLevelLights(dtReal: number): void {
    if (this.levelLights.length === 0) return
    const cam = this.camera.position

    // A jelenleg égő fények 30% kedvezményt kapnak a távolságukban —
    // ez a hiszterézis, ami megakadályozza a határon való oda-vissza váltást.
    const lit = new Set<number>()
    for (const s of this.levelSlots) if (s.owner >= 0) lit.add(s.owner)

    const score = (i: number): number => {
      const l = this.levelLights[i]
      const d = (l.x - cam.x) ** 2 + (l.y - cam.y) ** 2 + (l.z - cam.z) ** 2
      return lit.has(i) ? d * 0.7 : d
    }
    this.levelOrder.sort((a, b) => score(a) - score(b))
    const wanted = new Set(this.levelOrder.slice(0, this.levelSlots.length))

    // 1) A már nem kívánt foglalatok lecsengetése, majd felszabadítása.
    for (const s of this.levelSlots) {
      if (s.owner >= 0 && !wanted.has(s.owner)) {
        s.current = damp(s.current, 0, 0.0005, dtReal)
        if (s.current < 0.02) {
          s.owner = -1
          s.current = 0
        }
      }
    }

    // 2) A szabad foglalatokba a még ki nem osztott, kívánt fények.
    const assigned = new Set<number>()
    for (const s of this.levelSlots) if (s.owner >= 0) assigned.add(s.owner)
    for (const want of wanted) {
      if (assigned.has(want)) continue
      const free = this.levelSlots.find((s) => s.owner === -1)
      if (!free) break
      const l = this.levelLights[want]
      free.owner = want
      free.current = 0
      free.light.color.setHex(l.color)
      free.light.distance = l.distance
      free.light.position.set(l.x, l.y, l.z)
      assigned.add(want)
    }

    // 3) Animáció és felfutás.
    for (const s of this.levelSlots) {
      if (s.owner < 0) {
        s.light.intensity = s.current
        continue
      }
      const l = this.levelLights[s.owner]
      let target = l.base
      if (l.style === 'pulse') {
        target = l.base * (0.68 + 0.32 * Math.sin(this.time * 2.1 + l.phase))
      } else if (l.style === 'flicker') {
        // Villódzás: két különböző frekvenciájú szinusz szorzata — a
        // szabályos szinusz „diszkónak" hat, ez viszont hibás neonnak.
        const a = Math.sin(this.time * 17 + l.phase)
        const b = Math.sin(this.time * 5.3 + l.phase * 2.1)
        target = l.base * (a * b > 0.1 ? 1 : 0.35 + 0.25 * b)
      }
      // A felfutás gyors, de nem pillanatszerű — így a belépő fény nem
      // „kattan" be a látótérbe.
      s.current = damp(s.current, target, 0.0002, dtReal)
      s.light.intensity = s.current
    }
  }

  render(cam: CameraState, dtReal: number, hurt: number): void {
    this.time += dtReal
    this.effects.update(dtReal)
    this.applyCamera(cam, dtReal)

    this.updateLevelLights(dtReal)

    this.vmLight.intensity = Math.max(0, this.vmLight.intensity - dtReal * 26)
    this.postfx.update(dtReal, this.time, hurt)

    this.postfx.renderTo((r) => {
      r.render(this.scene, this.camera)
      // A statisztikát ITT olvassuk ki: a `present()` menete nullázná.
      this.sceneStats.calls = r.info.render.calls
      this.sceneStats.triangles = r.info.render.triangles
      // A kézifegyver mindig a világ ELŐTT: letisztított mélységgel rajzoljuk.
      r.clearDepth()
      r.render(this.vmScene, this.vmCamera)
    })
    this.postfx.present()
  }

  dispose(): void {
    this.unloadLevel()
    this.effects.dispose()
    this.viewModel.dispose()
    this.postfx.dispose()
    this.shadowTex.dispose()
    this.shadowMat.dispose()
    this.playerLight.dispose()
    this.renderer.dispose()
  }
}

function shortestAngle(from: number, to: number): number {
  let d = (to - from) % TAU
  if (d > Math.PI) d -= TAU
  if (d < -Math.PI) d += TAU
  return d
}

function projectileColor(type: string): number {
  switch (type) {
    case 'impFire':
      return 0xff8a2b
    case 'plasmaBolt':
      return 0x8affff
    case 'rocket':
      return 0xffd98a
    case 'bruteBolt':
      return 0xff3b1f
    default:
      return 0xffcc66
  }
}
